import Vue from "vue";
import openFeatureModalIfNeeded from "@/components/global/globalModals/openFeatureModalIfNeeded";
import { createRubric, getUser, updateUser } from "@/api";
import { initCrisp } from "@/init/crisp";
import { initCanny } from "@/init/canny";
import { setupHotjar } from "@/init/hotjar";
import {
  BASIC_PLAN,
  LMS_PROVIDERS,
  LSK_SUGGESTION_FORM_SELECTED_RUBRIC,
} from "@/constants";
import FirstQuiz from "@/components/dashboard/quiz/first_quiz.vue";

export const resetLocalStorageSavedFormData = () => {
  [LSK_SUGGESTION_FORM_SELECTED_RUBRIC].forEach((key) =>
    localStorage.removeItem(key)
  );
};

export const getWorkspaceProfileIdKey = (platformKey) => {
  if (platformKey === LMS_PROVIDERS.canvas.key) {
    return "canvasProfileId";
  }
  if (
    platformKey === LMS_PROVIDERS.google.key ||
    platformKey === LMS_PROVIDERS.googleClassroom.key
  ) {
    return "googleProfileId";
  }
  if (platformKey === LMS_PROVIDERS.schoology.key) {
    return "schoologyProfileId";
  }
  throw new Error(`Unknow platform ${platformKey}`);
};

export const mergeUserData = (vm, data) => {
  const clerkUser = vm.$getClerk().user;

  if (!clerkUser) {
    vm.$router.replace({ name: "login" });

    return;
  }

  const userData = {
    ...data,
    imageUrl: clerkUser.imageUrl,
    firstName: clerkUser.firstName,
    lastName: clerkUser.lastName,
    email: clerkUser.emailAddresses[0].emailAddress,
    unsafeMetadata: clerkUser.unsafeMetadata,
    showQuiz: clerkUser.unsafeMetadata?.quiz?.version === undefined,
    hasSeenTrialWelcome: !!clerkUser.unsafeMetadata?.hasSeenTrialWelcome,
    hasSeenTrialEnded: !!clerkUser.unsafeMetadata?.hasSeenTrialEnded,
  };

  return userData;
};

const isSharedRoute = (currentPath) => {
  const sharedRoutePatterns = ["/rubrics/shared/"];
  return sharedRoutePatterns.some((pattern) => currentPath.startsWith(pattern));
};

export const redirectToRoute = async (vm, user) => {
  // if still on non auth pages nothing to do
  if (vm.$route.meta.requiresAuth) {
    const data = vm.$store.state.user;
    const userData = mergeUserData(vm, data);
    await vm.$store.dispatch("setUser", userData);

    const newRubricData = localStorage.getItem("share:newRubricData");
    const useRubricData = localStorage.getItem("share:useRubricData");

    if (newRubricData) {
      localStorage.removeItem("share:newRubricData");
      const rubricData = JSON.parse(newRubricData);
      const { data } = await createRubric(rubricData);
      await vm.$router.push(`/rubric/edit/${data.id}`);
    } else if (useRubricData) {
      localStorage.removeItem("share:useRubricData");
      localStorage.setItem(LSK_SUGGESTION_FORM_SELECTED_RUBRIC, useRubricData);
      await vm.$router.push({ path: "/grading/manual-upload" });
    }

    return;
  }

  resetLocalStorageSavedFormData();
  vm.growthBook?.setAttributes({ email: user?.email || null });

  const { data } = await getUser("me");
  const userData = mergeUserData(vm, data);

  await vm.$store.dispatch("setUser", userData);
  await vm.$store.dispatch("languages/load");
  initCrisp(userData);
  initCanny(userData);
  setupHotjar(userData);

  if (!isSharedRoute(vm.$route.path)) {
    await vm.$router.replace({ name: "dashboard" });
  }

  openFeatureModalIfNeeded(vm);
};

export const updateUserUnsafeMetadata = (data) => {
  const clerkUser = Vue.prototype.$getClerk().user;

  return clerkUser.update({
    unsafeMetadata: {
      ...clerkUser.unsafeMetadata,
      ...data,
    },
  });
};

export const showQuizModalIfNeeded = (user) => {
  return new Promise((modalResolve) => {
    if (!user.showQuiz) {
      modalResolve(false);

      return;
    }

    Vue.prototype.$openForm("quiz-modal", {
      forceComponent: FirstQuiz,
      isModal: true,
      modalProps: {
        size: "content",
        hideTitle: true,
        hideFooter: true,
        closable: false,
        persistent: true,
      },
      on: {
        finish: async (result) => {
          try {
            // trigger trial experiment for basic plan only
            if (user.plan === BASIC_PLAN) {
              // ignore failures for feature flag update
              await updateUser({ role: result.answers.are_you_a }).catch(
                () => {}
              );
            }
            await updateUserUnsafeMetadata({
              quiz: { version: "1.0.0", answers: result.answers },
            });
          } catch (error) {
            console.error("error", error);
            result.resolve();
          } finally {
            modalResolve(true);
          }
        },
      },
      callbackEvent: "finish",
    });
  });
};

export default {
  mergeUserData,
  redirectToRoute,
  showQuizModalIfNeeded,
};
